import { Badge } from "@/components/ui/Badge";
import type { MedVisitCitation } from "@/services/medChronServiceV2.types";
import { cx } from "class-variance-authority";
import { Copy, Pen, Plus, Trash2 } from "lucide-react";
import React from "react";

import type { MedVisitDetailResponse } from "../../../types/medchron";
import { formatDate } from "../../../utils/dateUtils";
import { HighlightText } from "../HighlightText";
import { VisitBulkSelectCheckbox } from "./VisitBulkSelectCheckbox";
import { VisitCitationInlineList } from "./VisitCitationList";
import { VisitDetailRow } from "./VisitDetailRow";
import { VisitHeaderForm } from "./VisitHeaderForm";
import type { VisitHeaderSaveValues } from "./VisitHeaderForm.types";
import type {
  CaseFileOption,
  EditingVisitDetailState,
  MedChronTemplate,
  VisitCitationClickHandler,
  VisitDisplay,
  VisitFieldSuggestions,
} from "./types";
import { shouldVisitCardClickToggleBulkSelect } from "./utils/visitCardBulkSelectClick";
import { formatVisitType } from "./utils/visitFormatting";

export interface VisitCardProps {
  visit: VisitDisplay;
  editMode: boolean;
  fileMapping: Record<string, string>;
  onCitationClick: VisitCitationClickHandler;
  details: MedVisitDetailResponse[];
  editingVisitHeader: string | null;
  fieldSuggestions: VisitFieldSuggestions;
  editingVisitDetail: EditingVisitDetailState | null;
  setEditingVisitDetail: (v: EditingVisitDetailState | null) => void;
  newDetailCategory: string;
  setNewDetailCategory: (v: string) => void;
  newDetailText: string;
  setNewDetailText: (v: string) => void;
  showNewDetailForm: string | null;
  setShowNewDetailForm: (v: string | null) => void;
  isSavingDetail: boolean;
  onEditHeaderClick: (visit: VisitDisplay) => void;
  onVisitHeaderUpdate: (visitId: string, values: VisitHeaderSaveValues) => void;
  onCancelEditHeader: () => void;
  hasVisitTypeError: boolean;
  onClearVisitTypeError: () => void;
  handleVisitDetailUpdate: (
    visitId: string,
    oldCategory: string,
    newCategory: string,
    newDetails: string
  ) => void;
  onDeleteDetail: (versionId: string, visitId: string, category: string) => void;
  onDeleteVisit: (visit: VisitDisplay) => void;
  handleSubmitNewDetail: (visitId: string) => void;
  onAddDetail: (visitId: string) => void;
  autoResizeTextarea: (el: HTMLTextAreaElement | null) => void;
  availableFiles: CaseFileOption[];
  template: MedChronTemplate | null;
  visitBulkSelectChecked: boolean;
  visitBulkSelectIndeterminate: boolean;
  onVisitBulkSelectToggle: () => void;
  highlightQuery: string;
  isFlashTarget?: boolean;
  onFlashComplete?: () => void;
}

/** Props passed to every visit row; parent supplies `visit`, `details`, and bulk checkbox state per row. */
export type VisitCardSharedProps = Omit<
  VisitCardProps,
  | "visit"
  | "details"
  | "visitBulkSelectChecked"
  | "visitBulkSelectIndeterminate"
  | "onVisitBulkSelectToggle"
  | "isFlashTarget"
>;

export const VisitCard: React.FC<VisitCardProps> = ({
  visit,
  editMode,
  fileMapping,
  onCitationClick,
  details,
  editingVisitHeader,
  fieldSuggestions,
  editingVisitDetail,
  setEditingVisitDetail,
  newDetailCategory,
  setNewDetailCategory,
  newDetailText,
  setNewDetailText,
  showNewDetailForm,
  setShowNewDetailForm,
  isSavingDetail,
  onEditHeaderClick,
  onVisitHeaderUpdate,
  onCancelEditHeader,
  hasVisitTypeError,
  onClearVisitTypeError,
  handleVisitDetailUpdate,
  onDeleteDetail,
  onDeleteVisit,
  handleSubmitNewDetail,
  onAddDetail,
  autoResizeTextarea,
  availableFiles,
  template,
  visitBulkSelectChecked,
  visitBulkSelectIndeterminate,
  onVisitBulkSelectToggle,
  highlightQuery,
  isFlashTarget = false,
  onFlashComplete,
}) => {
  const isEditingHeader = editingVisitHeader === visit.visit_id;

  const handleVisitCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      !shouldVisitCardClickToggleBulkSelect(event.target, {
        editMode,
        isEditingHeader,
      })
    ) {
      return;
    }
    onVisitBulkSelectToggle();
  };

  const handleCitationClick = (citation: MedVisitCitation) =>
    onCitationClick(citation, visit.visit_id);
  const primaryCitation = visit.citations[0];
  const headlineProps = primaryCitation
    ? {
        role: "button",
        tabIndex: 0,
        onClick: () => handleCitationClick(primaryCitation),
        onKeyDown: (e: React.KeyboardEvent) =>
          e.key === "Enter" && handleCitationClick(primaryCitation),
      }
    : {};
  const headlineClassName = cx(
    "text-base font-bold tracking-tight text-black",
    primaryCitation && "cursor-pointer transition-colors hover:text-primary"
  );

  const headerContent = () => {
    if (isEditingHeader) {
      return (
        <div data-visit-card-no-bulk-select>
          <VisitHeaderForm
            visit={visit}
            availableFiles={availableFiles}
            fileMapping={fileMapping}
            fieldSuggestions={fieldSuggestions}
            template={template}
            onSave={onVisitHeaderUpdate}
            onCancel={onCancelEditHeader}
            hasVisitTypeError={hasVisitTypeError}
            onClearVisitTypeError={onClearVisitTypeError}
          />
        </div>
      );
    }

    if (visit.isCollapsed) {
      return (
        <div className="flex items-center gap-2">
          <h3 data-visit-card-no-bulk-select {...headlineProps} className={headlineClassName}>
            {visit.visitCount}{" "}
            <HighlightText
              highlightQuery={highlightQuery}
              text={formatVisitType(template, visit.visit_type_id)}
            />
            {visit.visit_subtype && (
              <>
                {" "}
                (<HighlightText highlightQuery={highlightQuery} text={visit.visit_subtype} />)
              </>
            )}
            s,{" "}
            {visit.startDate ? (
              <HighlightText highlightQuery={highlightQuery} text={formatDate(visit.startDate)} />
            ) : (
              "Unknown"
            )}{" "}
            -{" "}
            {visit.endDate ? (
              <HighlightText highlightQuery={highlightQuery} text={formatDate(visit.endDate)} />
            ) : (
              "Unknown"
            )}
            ,{" "}
            <HighlightText
              highlightQuery={highlightQuery}
              text={visit.provider_name ?? "Unknown"}
            />
            {visit.facility_name && (
              <span className="ml-1 font-medium text-neutral-500">
                (<HighlightText highlightQuery={highlightQuery} text={visit.facility_name} />)
              </span>
            )}
          </h3>
          {editMode && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEditHeaderClick(visit);
              }}
              className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
            >
              <Pen size={14} strokeWidth={2.5} />
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <h3 data-visit-card-no-bulk-select {...headlineProps} className={headlineClassName}>
          {visit.visit_date ? (
            <HighlightText highlightQuery={highlightQuery} text={formatDate(visit.visit_date)} />
          ) : (
            "Unknown date"
          )}
          ,{" "}
          <HighlightText
            highlightQuery={highlightQuery}
            text={formatVisitType(template, visit.visit_type_id)}
          />
          {visit.visit_subtype && (
            <>
              {" "}
              (<HighlightText highlightQuery={highlightQuery} text={visit.visit_subtype} />)
            </>
          )}
          ,{" "}
          <HighlightText highlightQuery={highlightQuery} text={visit.provider_name ?? "Unknown"} />
          {visit.facility_name && (
            <span className="ml-1 font-medium text-neutral-500">
              (<HighlightText highlightQuery={highlightQuery} text={visit.facility_name} />)
            </span>
          )}
        </h3>
        {visit.sourceVersionName && (
          <Badge
            tone="neutral"
            shape="tag"
            variant="outline"
            icon={<Copy size={12} />}
            className="shrink-0 opacity-60"
          >
            Copied from {visit.sourceVersionName}
          </Badge>
        )}
        {editMode && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEditHeaderClick(visit);
            }}
            className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
          >
            <Pen size={14} strokeWidth={2.5} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      className={cx(
        "group relative rounded-2xl border border-neutral-200/80 p-4 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06)] transition-all duration-300 hover:border-primary-border/60 hover:shadow-[0_8px_24px_-4px_rgba(0,0,0,0.08)]",
        {
          "bg-card": !visitBulkSelectChecked,
          "bg-primary-subtle/60": visitBulkSelectChecked,
        },
        isFlashTarget && "animate-card-flash"
      )}
      data-visit-id={visit.visit_id}
      onClick={handleVisitCardClick}
      onAnimationEnd={(e) => {
        if (isFlashTarget && e.target === e.currentTarget) onFlashComplete?.();
      }}
    >
      <div className={editMode ? "flex gap-3" : undefined}>
        {editMode && (
          <div
            className="flex shrink-0 flex-col items-center pt-0.5"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <VisitBulkSelectCheckbox
              ariaLabel="Select visit for bulk actions"
              checked={visitBulkSelectChecked}
              indeterminate={visitBulkSelectIndeterminate}
              onToggle={onVisitBulkSelectToggle}
            />
          </div>
        )}
        <div className={editMode ? "min-w-0 flex-1" : undefined}>
          {editMode && (
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                aria-label="Delete this visit"
                onClick={() => onDeleteVisit(visit)}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-neutral-200 bg-card px-2.5 py-1.5 text-xs font-medium text-neutral-500 shadow-sm transition-colors duration-200 hover:border-destructive-border hover:bg-destructive-subtle hover:text-destructive-text focus-visible:shadow-focus-danger focus-visible:outline-none"
              >
                <Trash2 size={14} strokeWidth={2.5} />
                Delete
              </button>
            </div>
          )}
          <div className="mb-3">
            {headerContent()}
            <div
              data-visit-card-no-bulk-select
              className="mt-1 flex items-center gap-2 text-xs font-semibold text-neutral-500"
            >
              <span className="shrink-0 rounded bg-neutral-100 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-micro text-black">
                Source
              </span>
              <VisitCitationInlineList
                citations={visit.citations}
                fileMapping={fileMapping}
                onCitationClick={handleCitationClick}
                highlightQuery={highlightQuery}
              />
            </div>
          </div>
          <div className="space-y-2">
            {details.map((detail) => (
              <VisitDetailRow
                key={detail.visit_detail_category}
                detail={detail}
                visit={visit}
                editMode={editMode}
                editingVisitDetail={editingVisitDetail}
                setEditingVisitDetail={setEditingVisitDetail}
                template={template}
                handleVisitDetailUpdate={handleVisitDetailUpdate}
                onDeleteDetail={onDeleteDetail}
                isSavingDetail={isSavingDetail}
                autoResizeTextarea={autoResizeTextarea}
                highlightQuery={highlightQuery}
              />
            ))}
            {editMode && showNewDetailForm === visit.visit_id && (
              <div
                data-visit-card-no-bulk-select
                className="rounded-lg border border-neutral-200 bg-neutral-50 p-3"
              >
                <input
                  type="text"
                  placeholder="Category"
                  value={newDetailCategory}
                  onChange={(e) => setNewDetailCategory(e.target.value)}
                  className="mb-2 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Details"
                  value={newDetailText}
                  onChange={(e) => setNewDetailText(e.target.value)}
                  className="mb-2 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleSubmitNewDetail(visit.visit_id)}
                    disabled={isSavingDetail}
                    className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
                  >
                    {isSavingDetail ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewDetailForm(null)}
                    disabled={isSavingDetail}
                    className="rounded-md border border-neutral-300 bg-card px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {editMode && (
              <button
                type="button"
                onClick={() => onAddDetail(visit.visit_id)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-neutral-200 py-3 text-sm font-bold text-neutral-400 transition-all hover:border-primary-border hover:bg-primary-subtle/50 hover:text-primary"
              >
                <Plus size={18} strokeWidth={2.5} /> Add Detail
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
